import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Heading3, tokens } from '@treams/treams-ui';

import { StyrBadge } from './StyrBadge';

export interface StyrLinkedEntity {
	id: string;
	title: string;
}

const SummaryGrid = styled(Box)`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 16px;
`;

const SummaryCard = styled(Box)`
	background: ${tokens.surface.primary};
	border: 1px solid ${tokens.border.subtle};
	border-radius: 6px;
	padding: 16px 20px;
`;

const CardHeader = styled(Flex)`
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`;

const StatValue = styled.span`
	font-size: 22px;
	font-weight: 600;
	color: ${tokens.text.secondary};
	line-height: 1.1;
`;

const SectionTitle = styled.h3`
	margin: 0;
	font-size: 15px;
	font-weight: 600;
	color: ${tokens.text.secondary};
`;

const ItemList = styled.ul`
	list-style: none;
	margin: 12px 0 0;
	padding: 0;
	max-height: 220px;
	overflow-y: auto;
`;

const Item = styled.li`
	font-size: 13px;
	color: ${tokens.text.secondary};
	padding: 6px 0;
	border-top: 1px solid ${tokens.border.subtle};
`;

const EmptyText = styled.span`
	font-size: 13px;
	color: ${tokens.text.secondary};
	opacity: 0.7;
`;

const MAX_VISIBLE = 50;

function LinkedColumn(props: {
	title: string;
	emptyLabel: string;
	moreLabel: (hidden: number) => string;
	items: StyrLinkedEntity[];
}) {
	const visible = props.items.slice(0, MAX_VISIBLE);
	const hidden = props.items.length - visible.length;

	return (
		<SummaryCard>
			<CardHeader>
				<SectionTitle>{props.title}</SectionTitle>
				<StatValue>{props.items.length}</StatValue>
			</CardHeader>

			{props.items.length === 0 ? (
				<Box mt={2}>
					<EmptyText>{props.emptyLabel}</EmptyText>
				</Box>
			) : (
				<ItemList>
					{visible.map((item) => (
						<Item key={item.id}>{item.title}</Item>
					))}
					{hidden > 0 && (
						<Item>
							<EmptyText>{props.moreLabel(hidden)}</EmptyText>
						</Item>
					)}
				</ItemList>
			)}
		</SummaryCard>
	);
}

/**
 * Live "what is connected" view for the Styr page. Instead of an aggregated sync summary, it
 * lists the Treams competencies that are currently linked to Styr (i.e. carry a `styrSkillID`),
 * derived from the live entities — so it always reflects the current state regardless of when
 * the last sync ran.
 * @param props - Component props.
 * @param props.competencies - Linked Treams competencies to show.
 */
export default function StyrCompetencyMapping(props: { competencies: StyrLinkedEntity[] }) {
	const { t } = useTranslation();

	return (
		<Box>
			<Flex alignItems="center" mb={2} style={{ gap: 8 }}>
				<Heading3>{t('settings.connectors.styr.linked.title')}</Heading3>
				<StyrBadge />
			</Flex>

			<SummaryGrid>
				<LinkedColumn
					title={t('settings.connectors.styr.linked.competencies')}
					emptyLabel={t('settings.connectors.styr.linked.noCompetencies')}
					moreLabel={(hidden) =>
						t('settings.connectors.styr.linked.more', {
							count: hidden,
						})
					}
					items={props.competencies}
				/>
			</SummaryGrid>
		</Box>
	);
}
